export function normalizePointAudiosForSave(point = {}) {
  const fromAudios = Array.isArray(point?.audios) ? point.audios : [];
  const fromMultimedia = Array.isArray(point?.multimedia)
    ? point.multimedia
        .filter((item) => {
          const type = String(item?.mediaType || "").toLowerCase();
          const mime = String(item?.mimeType || "").toLowerCase();
          return type === "audio" || mime.startsWith("audio/");
        })
        .map((item) => ({
          id: item?.sourceId || item?.id || "",
          url: item?.url || "",
          language: String(item?.language || item?.languageIso || "")
            .toLowerCase()
            .trim(),
        }))
    : [];
  const mixedItems = Array.isArray(point?.mixed_audio)
    ? point.mixed_audio
    : point?.mixed_audio && typeof point.mixed_audio === "object"
      ? [point.mixed_audio]
      : [];
  const mixedIds = new Set(mixedItems.map((item) => String(item?.id || "")));
  const seenFiles = new Set();
  const audios = [];
  // The Upload audio collection is the source of truth for the `audios`
  // payload. Preserve multiple uploads even when they share a language;
  // exclude only mixed files and exact duplicate files across both sources.
  [...fromMultimedia, ...fromAudios].forEach((item) => {
    const language = String(item?.language || item?.languageIso || "")
      .toLowerCase()
      .trim();
    if (!language) return;
    const id = String(item?.id || item?.sourceId || "");
    if (id && mixedIds.has(id)) return;
    const url = String(item?.url || "");
    const fileKey = id || url;
    if (fileKey && seenFiles.has(fileKey)) return;
    if (fileKey) seenFiles.add(fileKey);
    audios.push({
      id,
      url,
      language,
    });
  });
  return audios;
}

export function readRoutePointExportValue(entry) {
  if (typeof entry === "string") return entry;
  if (!entry || typeof entry !== "object") return "";
  return String(entry?.value ?? entry?.message ?? entry?.text ?? "");
}

export function buildRoutePointExportTranslations(field = {}, languages = []) {
  const source = field && typeof field === "object" ? field : {};
  return (Array.isArray(languages) ? languages : []).reduce(
    (translations, language) => {
      const iso = String(language?.lng_iso || language?.iso || "")
        .toLowerCase()
        .trim();
      if (!iso) return translations;
      const localizedEntry = Array.isArray(source)
        ? source.find(
            (entry) =>
              entry &&
              typeof entry === "object" &&
              Object.prototype.hasOwnProperty.call(entry, iso),
          )?.[iso]
        : source?.[iso];
      translations[iso] = readRoutePointExportValue(localizedEntry);
      return translations;
    },
    {},
  );
}

export function getRoutePointExportMedia(point = {}) {
  const multimedia = Array.isArray(point?.multimedia) ? point.multimedia : [];
  const images = [
    ...(Array.isArray(point?.images) ? point.images : []),
    ...multimedia.filter((item) => {
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const mimeType = String(item?.mimeType || "").toLowerCase();
      return mediaType === "image" || mimeType.startsWith("image/");
    }),
  ];
  const videos = [
    ...(Array.isArray(point?.videos) ? point.videos : []),
    ...multimedia.filter((item) => {
      const mediaType = String(item?.mediaType || "").toLowerCase();
      const mimeType = String(item?.mimeType || "").toLowerCase();
      return mediaType === "video" || mimeType.startsWith("video/");
    }),
  ];
  const audioUrls = normalizePointAudiosForSave(point).reduce((urls, audio) => {
    const iso = String(audio?.language || "").toLowerCase().trim();
    const url = String(audio?.url || "").trim();
    if (iso && url) urls[iso] = url;
    return urls;
  }, {});
  const uniqueUrls = (items) =>
    Array.from(
      new Set(
        items
          .map((item) => String(item?.url || item || "").trim())
          .filter(Boolean),
      ),
    );
  return {
    photoUrls: uniqueUrls(images),
    audioUrls,
    videoUrls: uniqueUrls(videos),
  };
}

export function buildRoutePointExportFileName({
  selectedStartPoint,
  selectedStartTour,
  activeWalkingRouteMetaByKey,
  selectedWalkingRouteKey,
  selectedMode,
}) {
  const startPointName = readRoutePointExportValue(selectedStartPoint?.name?.en);
  const tourNames = selectedStartTour?.name || {};
  const tourName =
    readRoutePointExportValue(tourNames?.en) ||
    readRoutePointExportValue(tourNames) ||
    (tourNames && typeof tourNames === "object"
      ? Object.values(tourNames).map(readRoutePointExportValue).find(Boolean)
      : "") ||
    "";
  const walkingRouteNames =
    activeWalkingRouteMetaByKey?.[selectedWalkingRouteKey]?.name || {};
  const walkingRouteName =
    readRoutePointExportValue(walkingRouteNames?.en) ||
    Object.values(walkingRouteNames)
      .map(readRoutePointExportValue)
      .find(Boolean) ||
    "";
  const exportName =
    selectedMode === "random"
      ? `${tourName || "Tour"}-Point-of-Interest`
      : (selectedMode === "walking" ? walkingRouteName : "") ||
        startPointName ||
        selectedStartPoint?.id ||
        "route";
  const safeStartPointName = String(exportName)
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  if (selectedMode === "random") {
    return `${safeStartPointName || "Tour-Point-of-Interest"}.json`;
  }
  return `${safeStartPointName || "route"}-${selectedMode || "points"}.json`;
}

export function buildRoutePointExportPayload(points, languages) {
  return {
    points: points.map((point, index) => ({
      id: String(point?.serverId || point?.uuid || point?.id || ""),
      pointNumber: Number(point?.order || index + 1) || index + 1,
      pointType: String(point?.pointType || ""),
      labelCode: String(point?.labelCode || ""),
      name: buildRoutePointExportTranslations(point?.name, languages),
      description: buildRoutePointExportTranslations(point?.description, languages),
      infoText: buildRoutePointExportTranslations(point?.more_info, languages),
      notes: String(point?.notes || ""),
      ai_brief: String(point?.ai_brief ?? point?.aiBrief ?? ""),
      triggerRadiusMeters:
        Number(point?.radiusMeters ?? point?.radius_meters) || 0,
      turnType: Number(point?.turnCase ?? point?.turn_case) || 0,
      routeGroup: String(point?.routeGroup ?? point?.route_group ?? "main"),
      coordinate: {
        latitude: Number(point?.latitude),
        longitude: Number(point?.longitude),
      },
      media: getRoutePointExportMedia(point),
    })),
  };
}
